import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Blockquote from '@tiptap/extension-blockquote';
import Code from '@tiptap/extension-code';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

// Include Highlight.js
import hljs from 'highlight.js';
import hljsBlade from 'highlightjs-blade';

// load all highlight.js languages
import css from 'highlight.js/lib/languages/css';
import js from 'highlight.js/lib/languages/javascript';
import php from 'highlight.js/lib/languages/php';
import ts from 'highlight.js/lib/languages/typescript';
import html from 'highlight.js/lib/languages/xml';
import docker from 'highlight.js/lib/languages/dockerfile';
import blade from 'highlight.js/lib/languages/php';
import sql from 'highlight.js/lib/languages/sql';
import json from 'highlight.js/lib/languages/json';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';

import { lowlight } from 'lowlight';
import { toHtml } from 'hast-util-to-html';

import { debounce } from 'lodash';

hljs.registerLanguage('blade', hljsBlade);

lowlight.registerLanguage('html', html);
lowlight.registerLanguage('css', css);
lowlight.registerLanguage('js', js);
lowlight.registerLanguage('ts', ts);
lowlight.registerLanguage('php', php);
lowlight.registerLanguage('blade', blade);
lowlight.registerLanguage('sql', sql);
lowlight.registerLanguage('json', json);
lowlight.registerLanguage('xml', xml);
lowlight.registerLanguage('yaml', yaml);
lowlight.registerLanguage('docker', docker);

window.isEditorFocused = false;

function toHtmlWithCodeHighlighting(htmlContentRaw) {
    // Define a regular expression to match <pre><code> blocks
    const htmlContent = htmlContentRaw.replace(/&nbsp;/g, ' ');

    const codeBlockRegex =
        /<pre><code(?:\s+class="language-(\w+)")?>([\s\S]*?)<\/code><\/pre>/g;

    // Replace each match with highlighted code
    const highlightedHtml = htmlContent.replace(
        codeBlockRegex,
        (match, language, codeContent) => {
            // If language is not specified, auto-detect it using highlight.js
            const lang = language || detectLanguage(codeContent);

            // Use lowlight to highlight the code
            const highlightedCode = lowlight.highlight(lang, codeContent);

            // Return the highlighted code wrapped in <pre><code>
            return `<pre><code class="language-${lang}">${toHtml(
                highlightedCode
            )}</code></pre>`;
        }
    );

    return highlightedHtml;
}

// Function to auto-detect language using Highlight.js
function detectLanguage(code) {
    const result = hljs.highlightAuto(code);
    return result.language || 'plaintext';
}

function callEditor(content = '') {
    const editor = new Editor({
        element: document.querySelector('#comment-editor'),
        extensions: [
            StarterKit.configure({
                code: false,
                codeBlock: false,
                blockquote: false,
            }),
            Blockquote.extend({
                addKeyboardShortcuts() {
                    return {
                        'Mod-Shift-9': () =>
                            this.editor.commands.toggleBlockquote(),
                    };
                },
            }),
            // Code.extend({
            //     addKeyboardShortcuts() {
            //         return {
            //             'Mod-Shift-C': () => this.editor.commands.toggleCode(),
            //         };
            //     },
            // }),
            CodeBlockLowlight.configure({
                lowlight,
                defaultLanguage: 'js',
            }),
            Link.configure({
                openOnClick: false,
                validate: (href) => /^https?:\/\//.test(href),
            }),
            Placeholder.configure({
                placeholder: 'Add comment…',
            }),
        ],
        onFocus: () => {
            isEditorFocused = true;
        },
        onBlur: () => {
            isEditorFocused = false;
        },
        onTransaction({ editor, transaction }) {
            for (const action in editorActions) {
                const statusKey = editorActions[action].statusKey;
                setButtonStatus(action, editor.isActive(statusKey));
            }

            const commentEditor = document.querySelector('#comment-editor');
            commentEditor.style.height = '60px';
            commentEditor.style.height = commentEditor.scrollHeight + 'px';

            if (isTextSelected(editor)) {
                document.querySelector(
                    'button[aria-label="link"]'
                ).disabled = false;
            } else {
                document.querySelector(
                    'button[aria-label="link"]'
                ).disabled = true;
            }
        },
        onUpdate: debounce(({ editor }) => {
            // TODO: Update content on server
            console.log('Update Content on server');
            console.log('content - ', editor.getJSON());

            console.log(toHtmlWithCodeHighlighting(editor.getHTML()));

            // console.log('toHtml', toHtml);
            // console.log(toHtml(lowlight.highlight('js', editor.getHTML())));
        }, 1000),
        content: content,
    });

    window.editor = editor;
    window.editor.getHightlightedHTML = () => {
        return toHtmlWithCodeHighlighting(editor.getHTML());
    };

    const allEditorButtons = document.querySelectorAll(
        '#editor-wrapper button'
    );

    const editorActions = {
        bold: {
            toggle: () => editor.chain().focus().toggleBold().run(),
            statusKey: 'bold',
        },
        italic: {
            toggle: () => editor.chain().focus().toggleItalic().run(),
            statusKey: 'italic',
        },
        strike: {
            toggle: () => editor.chain().focus().toggleStrike().run(),
            statusKey: 'strike',
        },
        link: { toggle: setLink, statusKey: 'link' },
        orderedList: {
            toggle: () => editor.chain().focus().toggleOrderedList().run(),
            statusKey: 'orderedList',
        },
        bulletList: {
            toggle: () => editor.chain().focus().toggleBulletList().run(),
            statusKey: 'bulletList',
        },
        blockquote: {
            toggle: () => editor.chain().focus().toggleBlockquote().run(),
            statusKey: 'blockquote',
        },
        // code: {
        //     toggle: () => editor.chain().focus().toggleCode().run(),
        //     statusKey: 'code',
        // },
        codeBlock: {
            toggle: () => editor.chain().focus().toggleCodeBlock().run(),
            statusKey: 'codeBlock',
        },
    };

    allEditorButtons.forEach((button) => {
        button.addEventListener('click', (e) => {
            e.preventDefault();
            const action = editorActions[button.ariaLabel];
            if (action) {
                action.toggle();
                setButtonStatus(
                    button.ariaLabel,
                    editor.isActive(action.statusKey)
                );
            }
        });
    });
}

window.callEditor = callEditor;

function setLink() {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    // cancelled
    if (url === null) {
        return;
    }

    // empty
    if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
    }

    if (/^https?:\/\//.test(url)) {
        editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: url })
            .run();
    } else {
        window.alert('URL must start with "http://" or "https://"');
        setLink();
    }
}

function setButtonStatus(ariaLabel, isActive) {
    document.querySelector(`button[aria-label="${ariaLabel}"]`).ariaChecked =
        isActive;
}

function isTextSelected(editor) {
    const { from, to } = editor.state.selection;
    return from !== to;
}
